export const FEE_METHODS = [
  { id: 'earned', label: 'Earned each month' },
  { id: 'end', label: 'Paid at the end' },
  { id: 'prorata', label: 'Prorata' },
];

const OPEN_COHORT_TOLERANCE = 0.01;

function roundMoney(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

function cohortKey(item) {
  return `${item?.source || ''}|${item?.cohortMonth || ''}|${item?.label || ''}`;
}

function addAmount(map, month, amount) {
  if (!month || !amount) return;
  map.set(month, roundMoney((map.get(month) || 0) + amount));
}

function blankCohort(key, label, fundingMonth) {
  return {
    key,
    label,
    fundingMonth: fundingMonth || '',
    balance: 0,
    unpaidFee: 0,
    feeByMonth: new Map(),
    repaymentByMonth: new Map(),
  };
}

function cohortsFromBreakdown(months) {
  const cohorts = new Map();
  const unallocated = blankCohort('__unallocated__', 'Unallocated', '');

  const ensure = (item) => {
    const key = cohortKey(item);
    if (!cohorts.has(key)) {
      cohorts.set(key, blankCohort(key, item.label || item.cohortMonth || 'Cohort', item.cohortMonth || ''));
    }
    return cohorts.get(key);
  };

  months.forEach((month) => {
    let listed = 0;
    (month.feeBreakdown || []).forEach((item) => {
      const amount = item?.amount || 0;
      if (!amount) return;
      listed = roundMoney(listed + amount);
      addAmount(ensure(item).feeByMonth, month.month, amount);
    });
    (month.returnsBreakdown || []).forEach((item) => {
      const amount = item?.amount || 0;
      if (!amount) return;
      addAmount(ensure(item).repaymentByMonth, month.month, amount);
    });
    const gap = roundMoney((month.fee || 0) - listed);
    if (Math.abs(gap) >= OPEN_COHORT_TOLERANCE) {
      addAmount(unallocated.feeByMonth, month.month, gap);
    }
  });

  const list = [...cohorts.values()];
  if (unallocated.feeByMonth.size) list.push(unallocated);
  return list;
}

function replayCohortFees(months) {
  const cohorts = [];
  const byFunding = new Map();

  const ensure = (fundingMonth, label) => {
    const key = fundingMonth || '__unallocated__';
    if (!byFunding.has(key)) {
      const cohort = blankCohort(key, label || (fundingMonth ? `Projected ${fundingMonth}` : 'Unallocated'), fundingMonth);
      byFunding.set(key, cohort);
      cohorts.push(cohort);
    }
    return byFunding.get(key);
  };

  const applyPayments = (parts, monthKey) => {
    (parts || []).forEach((item) => {
      const pay = roundMoney(item?.amount || 0);
      if (pay <= 0) return;
      const cohort = ensure(item.cohortMonth || monthKey, item.label);
      addAmount(cohort.repaymentByMonth, monthKey, pay);
      let principalPaid = cohort.balance;
      if (principalPaid > pay) principalPaid = pay;
      principalPaid = roundMoney(principalPaid);
      let feePaid = roundMoney(pay - principalPaid);
      if (feePaid > cohort.unpaidFee) feePaid = roundMoney(cohort.unpaidFee);
      cohort.balance = roundMoney(cohort.balance - principalPaid);
      cohort.unpaidFee = roundMoney(cohort.unpaidFee - feePaid);
    });
  };

  months.forEach((month) => {
    const opening = roundMoney(cohorts.reduce((sum, cohort) => sum + cohort.balance + cohort.unpaidFee, 0));
    const monthFee = roundMoney(month.fee || 0);
    if (monthFee > 0 && opening > OPEN_COHORT_TOLERANCE) {
      const open = cohorts.filter((cohort) => cohort.balance + cohort.unpaidFee > OPEN_COHORT_TOLERANCE);
      let allocated = 0;
      open.forEach((cohort, index) => {
        const exposure = roundMoney(cohort.balance + cohort.unpaidFee);
        let share = roundMoney(monthFee * exposure / opening);
        if (index === open.length - 1) share = roundMoney(monthFee - allocated);
        allocated = roundMoney(allocated + share);
        cohort.unpaidFee = roundMoney(cohort.unpaidFee + share);
        addAmount(cohort.feeByMonth, month.month, share);
      });
    } else if (Math.abs(monthFee) >= OPEN_COHORT_TOLERANCE) {
      addAmount(ensure('', 'Unallocated').feeByMonth, month.month, monthFee);
    }

    const parts = month.returnsBreakdown || [];
    const funding = roundMoney(month.growthInvestments || 0);
    const sameMonthFunding = parts.filter((item) => (item.cohortMonth || '') === month.month && !byFunding.has(month.month));
    const earlier = parts.filter((item) => !sameMonthFunding.includes(item));
    applyPayments(earlier, month.month);
    if (funding > 0) {
      const label = sameMonthFunding[0]?.label;
      const cohort = ensure(month.month, label);
      cohort.balance = roundMoney(cohort.balance + funding);
    }
    applyPayments(sameMonthFunding, month.month);
  });

  return cohorts;
}

function cohortList(customer) {
  const months = [...(customer?.months || [])].sort((left, right) => left.month.localeCompare(right.month));
  const hasBreakdown = months.some((month) => (month.feeBreakdown || []).length > 0);
  return hasBreakdown ? cohortsFromBreakdown(months) : replayCohortFees(months);
}

function positiveMonths(map) {
  return [...map.entries()]
    .filter(([, amount]) => amount > 0)
    .sort((left, right) => left[0].localeCompare(right[0]));
}

function placeCohortFee(cohort, method) {
  const placed = new Map();
  const totalFee = roundMoney([...cohort.feeByMonth.values()].reduce((sum, amount) => sum + amount, 0));
  if (!totalFee) return placed;

  if (method === 'earned') {
    cohort.feeByMonth.forEach((amount, month) => addAmount(placed, month, amount));
    return placed;
  }

  const repayments = positiveMonths(cohort.repaymentByMonth);
  if (!repayments.length) {
    cohort.feeByMonth.forEach((amount, month) => addAmount(placed, month, amount));
    return placed;
  }

  if (method === 'end') {
    addAmount(placed, repayments[repayments.length - 1][0], totalFee);
    return placed;
  }

  const totalCash = repayments.reduce((sum, [, amount]) => sum + amount, 0);
  const share = totalFee / totalCash;
  let allocated = 0;
  repayments.forEach(([, amount], index) => {
    let fee = roundMoney(share * amount);
    if (index === repayments.length - 1) fee = roundMoney(totalFee - allocated);
    allocated = roundMoney(allocated + fee);
    addAmount(placed, repayments[index][0], fee);
  });
  return placed;
}

export function feeAmountsByMonth(customer, method) {
  if (method === 'earned') {
    const earned = new Map();
    (customer?.months || []).forEach((month) => {
      if (month?.fee) addAmount(earned, month.month, month.fee);
    });
    return earned;
  }

  const combined = new Map();
  cohortList(customer).forEach((cohort) => {
    placeCohortFee(cohort, method).forEach((amount, month) => addAmount(combined, month, amount));
  });
  return combined;
}

export function buildFeeRows(customers, monthKeys, method) {
  const rows = (customers || []).map((customer) => {
    const byMonth = feeAmountsByMonth(customer, method);
    return {
      id: customer.customerId,
      label: customer.companyName || customer.customerId,
      values: (monthKeys || []).map((month) => {
        const amount = byMonth.get(month) || 0;
        return amount ? roundMoney(amount) : null;
      }),
    };
  });
  const total = (monthKeys || []).map((_, index) => {
    const sum = rows.reduce((acc, row) => acc + (row.values[index] || 0), 0);
    return sum ? roundMoney(sum) : null;
  });
  return [...rows, { id: 'total', label: 'Total', values: total }];
}
